/**
 * The account: what this workspace is called, and how it reads dates and money.
 *
 * ── WHY THIS MATTERS MORE THAN A SETTINGS SCREEN USUALLY DOES ─────────────
 *
 * `App.vue` hardcoded pt-BR / BRL / America/Sao_Paulo with a comment admitting it,
 * because nothing served these values — the columns had been on `tenant` since the
 * first migration and no route read them. So every date and price in the product was
 * formatted for one country, and the same column decides when a tenant's crons fire.
 *
 * ── O DOCUMENTO EM SI MORA EM `shared/workspace` ──────────────────────────
 *
 * A leitura de `/v3/workspace` e os três interruptores de exibição do agente ficam em
 * `@/shared/workspace/use-workspace-doc`, porque `features/agents` também os lê e uma
 * feature não importa outra. Este arquivo continua sendo o dono das ESCRITAS e das
 * outras consultas da conta; a leitura é reexportada para quem já a importava daqui.
 *
 * "Ver como cliente" é deliberadamente um estado local de uma tela, e não um
 * interruptor do workspace: guardá-lo faria a espiada de uma pessoa mudar o que todo
 * mundo vê.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import type { VisualIdentity } from "@/shared/contracts/visual-identity";
import { request } from "@/shared/http/client";
import {
	type AgentDisplay,
	type Signature,
	useWorkspaceDoc,
	type Workspace,
	workspaceSelfKey,
} from "@/shared/workspace/use-workspace-doc";

export type {
	AgentDisplay,
	Signature,
	SignatureMode,
	Workspace,
} from "@/shared/workspace/use-workspace-doc";
export {
	DEFAULT_AGENT_DISPLAY,
	DEFAULT_SIGNATURE,
	signatureLabel,
	useAgentDisplay,
} from "@/shared/workspace/use-workspace-doc";

/** A leitura do documento, sob o nome que as telas desta feature já importavam. */
export const useWorkspace = useWorkspaceDoc;

export interface TeamMember {
	readonly email: string | null;
	readonly id: string;
	readonly name: string;
	readonly role: string;
	readonly status: string;
	readonly lastSeenAt: string | null;
	/** O rosto, como id de arquivo. Nulo é o caso comum, e a inicial cobre. */
	readonly avatarFileId: string | null;
}

/**
 * You, as this workspace knows you.
 *
 * The e-mail is read-only on purpose: it is the key the login path finds a person
 * by, so changing it is re-proving control of an address rather than editing this
 * record. The phone is not a credential — nobody signs in with it — and it is how
 * a rule reaches you on WhatsApp when you are not looking at the panel.
 */
export interface Profile {
	readonly id: string;
	readonly name: string;
	readonly role: string;
	readonly status: string;
	readonly phone: string | null;
	readonly email: string | null;
	/** Sua foto, como id de arquivo — nunca uma URL (I-S5). */
	readonly avatarFileId: string | null;
	/**
	 * O idioma em que ESTA PESSOA lê o painel (`app_user.language`, migração 0086).
	 *
	 * `null` é o caso comum e não quer dizer inglês: quer dizer que ninguém
	 * escolheu, e aí o painel segue o idioma do WORKSPACE, depois o do navegador,
	 * depois o inglês. Nunca decide o que o CLIENTE recebe — isso é
	 * `workspace.language`, e confundir os dois é o erro que a migração 0086
	 * existe para impedir.
	 */
	readonly language: string | null;
}

/** What the workspace says its business is, and what it forbids saying. */
export interface Business {
	readonly businessProfile: string | null;
	readonly brandRules: string | null;
	readonly visualIdentity?: VisualIdentity | null;
	/**
	 * O logo do workspace, já resolvido em URL pelo servidor (o `<img>` não manda
	 * Bearer, então é uma URL assinada). Null quando o onboarding não capturou nenhum.
	 * Só leitura: quem o define é a ferramenta do onboarding, não esta tela.
	 */
	readonly logoUrl?: string | null;
}

export const workspaceKeys = {
	// A mesma chave que a leitura compartilhada usa — copiar o literal aqui seria a
	// forma de um PATCH invalidar um cache que ninguém está lendo.
	self: workspaceSelfKey,
	team: ["workspace", "team"] as const,
	profile: ["workspace", "profile"] as const,
	business: ["workspace", "business"] as const,
};

/**
 * The colleagues.
 *
 * Used by the account screen AND by handing a conversation over — which is why it is
 * a workspace concern rather than something the inbox owns. `staleTime` is short
 * because "who is here" changes when someone accepts an invite.
 */
export function useTeam() {
	return useQuery({
		queryKey: workspaceKeys.team,
		queryFn: () => request<{ team: TeamMember[] }>("/v3/workspace/team"),
		select: (data) => data.team,
		staleTime: 60_000,
	});
}

export function useSaveWorkspace() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (patch: Partial<Workspace>) =>
			request<{ workspace: Workspace }>("/v3/workspace", { method: "PATCH", body: patch }),
		// Everything is invalidated, not just the workspace: changing the timezone
		// changes what every cached date MEANS, and a list still showing yesterday's
		// grouping under a new zone is worse than a reload.
		onSuccess: () => client.invalidateQueries(),
	});
}

/**
 * The three display switches, on their own.
 *
 * `useSaveWorkspace` clears the whole cache because the timezone changes what
 * every cached date MEANS. Nothing of the sort happens here — flipping "mostrar
 * o raciocínio" must not refetch the inbox — so only the workspace is refreshed.
 */
export function useSaveAgentDisplay() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (agentDisplay: AgentDisplay) =>
			request<{ workspace: Workspace }>("/v3/workspace", {
				method: "PATCH",
				body: { agentDisplay },
			}),
		onSuccess: () => client.invalidateQueries({ queryKey: workspaceKeys.self }),
	});
}

/**
 * Se o cliente fica sabendo quem está respondendo (migração 0057).
 *
 * Mesma razão de `useSaveAgentDisplay` para invalidar só o workspace: mudar a
 * assinatura não muda o significado de nenhuma data em cache, e varrer o cache
 * inteiro faria um seletor de duas opções recarregar o inbox.
 */
export function useSaveSignature() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (signature: Signature) =>
			request<{ workspace: Workspace }>("/v3/workspace", {
				method: "PATCH",
				body: { signature },
			}),
		onSuccess: () => client.invalidateQueries({ queryKey: workspaceKeys.self }),
	});
}

/**
 * Changing a colleague's role, or putting them out.
 *
 * Only the team list is invalidated. `useSaveWorkspace` above clears everything
 * on purpose because the timezone changes the meaning of every cached date;
 * nothing of the sort happens here, and sweeping the whole cache would make one
 * row's dropdown refetch the inbox.
 *
 * The server refuses (409) to leave the workspace without an active owner, so
 * this can be called optimistically-free: the answer is the truth.
 */
export function useUpdateTeamMember() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { id: string; role?: string; status?: string }) =>
			request<{ member: TeamMember }>(`/v3/workspace/team/${input.id}`, {
				method: "PATCH",
				body: { role: input.role, status: input.status },
			}),
		onSuccess: () => client.invalidateQueries({ queryKey: workspaceKeys.team }),
	});
}

/** Excluir da equipe: some da lista, perde o login e libera a vaga (0306 no back). */
export function useRemoveTeamMember() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			request<{ removed: string }>(`/v3/workspace/team/${id}`, { method: "DELETE" }),
		onSuccess: () => client.invalidateQueries({ queryKey: workspaceKeys.team }),
	});
}

/**
 * Why a change to a colleague was refused, in words.
 *
 * Mapped from the server's CODE, never shown as the server's own sentence — the
 * API answers in English, and `LAST_OWNER` is not an accident anybody triggers
 * once: it is what a workspace with a single owner gets every time it tries.
 * Same reasoning, and same shape, as `describeInviteError` in the auth feature.
 *
 * The `t` is BORROWED from whoever called, the precedent `features/auth/api/errors.ts`
 * set: this does not run inside a `setup`, so it cannot open a composer of its own.
 */
export function describeTeamError(e: unknown, say: (key: string) => string): string {
	const body = (e as { payload?: { error?: string } }).payload;
	if (body?.error === "LAST_OWNER") return say("teams.error.lastOwner");
	const kind = (e as { kind?: string }).kind;
	if (kind === "forbidden") return say("teams.error.forbidden");
	if (kind === "notFound") return say("teams.error.notFound");
	if (kind === "network" || kind === "timeout") return say("teams.error.network");
	return say("teams.error.failed");
}

/**
 * Você, como este workspace te conhece.
 *
 * `enabled` existe por causa de um leitor novo: a MOLDURA lê este documento para
 * resolver o idioma do painel, e ela também é montada no formulário público, no
 * chat público e na tela de entrar. Sem a condição, um navegador deslogado
 * pergunta quem é e recebe um 401 — barulho no console de quem nem entrou.
 * Omitido, continua como sempre foi: liga.
 */
export function useProfile(enabled?: () => boolean) {
	return useQuery({
		queryKey: workspaceKeys.profile,
		queryFn: () => request<{ profile: Profile }>("/v3/auth/profile"),
		select: (data) => data.profile,
		staleTime: 5 * 60_000,
		enabled: enabled ?? true,
	});
}

export function useUpdateProfile() {
	const client = useQueryClient();
	return useMutation({
		// Each field is written only when it is sent, so saving a phone number does
		// not blank a name this form did not carry.
		mutationFn: (patch: {
			name?: string;
			phone?: string | null;
			avatarFileId?: string | null;
			language?: string | null;
		}) => request<{ profile: Profile }>("/v3/auth/profile", { method: "PATCH", body: patch }),
		// The team list carries the same name, and seeing the old one there right
		// after renaming yourself reads as a failed save.
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: workspaceKeys.profile });
			void client.invalidateQueries({ queryKey: workspaceKeys.team });
		},
	});
}

/**
 * Changing your own password.
 *
 * Nothing is invalidated because nothing cached changed — what changed is that
 * every refresh token of this person, including this browser's, was revoked. The
 * screen signs out afterwards; see `YourAccountCard.vue`.
 */
export function useChangePassword() {
	return useMutation({
		mutationFn: (input: { currentPassword: string; newPassword: string }) =>
			request<void>("/v3/auth/change-password", { method: "POST", body: input }),
	});
}

/**
 * Asking to sign in with another address: the server checks the password and
 * mails a link to the new one. Nothing cached changes until that link is opened.
 */
export function useRequestEmailChange() {
	return useMutation({
		mutationFn: (input: { currentPassword: string; newEmail: string }) =>
			request<{ sentTo: string }>("/v3/auth/change-email", { method: "POST", body: input }),
	});
}

/**
 * The two paragraphs every agent of this workspace is told before its persona.
 *
 * Separate from `useWorkspace` because they are served separately, and for the
 * reason `business.ports.ts` gives: preferences are validated values, these are
 * free text that cannot be wrong.
 */
export function useBusiness() {
	return useQuery({
		queryKey: workspaceKeys.business,
		queryFn: () => request<{ business: Business }>("/v3/workspace/business"),
		select: (data) => data.business,
		staleTime: 5 * 60_000,
	});
}

export function useSaveBusiness() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (patch: Partial<Business>) =>
			request<{ business: Business }>("/v3/workspace/business", { method: "PATCH", body: patch }),
		onSuccess: () => client.invalidateQueries({ queryKey: workspaceKeys.business }),
	});
}
